import {useState, useCallback} from 'react';
import {PAGINATION_CONFIG} from '../../constants/appConfig';

export interface UseTablePaginationProps {
  initialPage?: number;
  initialPageSize?: number;
  totalCount?: number;
  onPageChange?: (page: number, pageSize: number) => void;
}

export interface UseTablePaginationReturn {
  currentPage: number;
  pageSize: number;
  totalPages: number;
  handlePageChange: (page: number) => void;
  handlePageSizeChange: (pageSize: number) => void;
  resetToFirstPage: () => void;
  paginationInfo: {
    startItem: number;
    endItem: number;
    totalCount: number;
  };
}

export const useTablePagination = ({
  initialPage = 1,
  initialPageSize = PAGINATION_CONFIG.DEFAULT_PAGE_SIZE,
  totalCount = 0,
  onPageChange
}: UseTablePaginationProps = {}): UseTablePaginationReturn => {
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [pageSize, setPageSize] = useState(initialPageSize);

  // pageSize === 0 means "All" — a single page containing every row.
  const totalPages = pageSize > 0 ? Math.ceil(totalCount / pageSize) : 1;

  const handlePageChange = useCallback((page: number) => {
    const validPage = Math.max(1, Math.min(page, totalPages));
    setCurrentPage(validPage);

    if (onPageChange) {
      onPageChange(validPage, pageSize);
    }
  }, [totalPages, pageSize, onPageChange]);

  const handlePageSizeChange = useCallback((newPageSize: number) => {
    // 0 = "All": pass it through unclamped; otherwise clamp to [MIN, MAX].
    const validPageSize = newPageSize <= 0
      ? 0
      : Math.max(
        PAGINATION_CONFIG.MIN_PAGE_SIZE,
        Math.min(newPageSize, PAGINATION_CONFIG.MAX_PAGE_SIZE)
      );

    setPageSize(validPageSize);
    setCurrentPage(1); // Reset to first page when changing page size

    if (onPageChange) {
      onPageChange(1, validPageSize);
    }
  }, [onPageChange]);

  const resetToFirstPage = useCallback(() => {
    setCurrentPage(1);
    if (onPageChange) {
      onPageChange(1, pageSize);
    }
  }, [pageSize, onPageChange]);

  const startItem = pageSize > 0 ? (currentPage - 1) * pageSize + 1 : (totalCount > 0 ? 1 : 0);
  const endItem = pageSize > 0 ? Math.min(currentPage * pageSize, totalCount) : totalCount;

  return {
    currentPage,
    pageSize,
    totalPages,
    handlePageChange,
    handlePageSizeChange,
    resetToFirstPage,
    paginationInfo: {
      startItem,
      endItem,
      totalCount
    }
  };
};

